import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  BlurOn as BlurIcon,
  PhotoLibrary as AlbumIcon,
  Star as QualityIcon,
  EmojiEmotions as SmileIcon,
  Group as PeopleIcon,
  Category as EventIcon
} from '@mui/icons-material';
import AuthenticatedImage from './AuthenticatedImage';

export default function QualityReportTab({ 
  sessionId, 
  qualityReport, 
  loading, 
  onLoad,
  getAuthHeaders 
}) {
  
  useEffect(() => {
    if (onLoad && !qualityReport && !loading) {
      onLoad();
    }
  }, [onLoad, qualityReport, loading]);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading quality analysis report...</Typography>
      </Box>
    );
  }

  if (!qualityReport) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Generate embeddings first to see quality analysis report.
        </Alert>
      </Box>
    );
  }

  const { stats, excluded } = qualityReport;
  const qualityPercentage = (quality) => 
    stats.total > 0 ? Math.round((stats.qualityDistribution[quality] / stats.total) * 100) : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Image Quality Analysis Report
      </Typography>
      
      {/* Summary Statistics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BlurIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">{stats.blurry}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Blurry Images ({Math.round((stats.blurry / stats.total) * 100)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AlbumIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">{stats.notAlbumWorthy}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Not Album Worthy ({Math.round((stats.notAlbumWorthy / stats.total) * 100)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <QualityIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">{stats.lowQuality}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Low Quality &lt; 5/10 ({Math.round((stats.lowQuality / stats.total) * 100)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SmileIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">{stats.total - stats.noSmile}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                With Smiles ({Math.round(((stats.total - stats.noSmile) / stats.total) * 100)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quality Score Distribution */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <QualityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Quality Score Distribution
          </Typography>
          {Object.entries(stats.qualityDistribution).map(([score, count]) => (
            <Box key={score} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Score {score}/10</Typography>
                <Typography variant="body2">{count} images ({qualityPercentage(score)}%)</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={qualityPercentage(score)} 
                color={score >= 7 ? 'success' : score >= 5 ? 'primary' : 'error'}
              />
            </Box>
          ))}
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Event Type Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Event Types
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(stats.byEventType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([type, count]) => (
                    <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Chip label={type} size="small" />
                      <Typography variant="body2">{count} ({Math.round((count / stats.total) * 100)}%)</Typography>
                    </Box>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Person Count Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                People in Photos
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(stats.personCountDistribution)
                  .sort((a, b) => {
                    if (a[0] === 'many') return 1;
                    if (b[0] === 'many') return -1;
                    return parseInt(a[0]) - parseInt(b[0]);
                  })
                  .map(([count, total]) => (
                    <Box key={count} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{count === '0' ? 'No people' : `${count} ${count === '1' ? 'person' : 'people'}`}</Typography>
                      <Typography variant="body2">{total} ({Math.round((total / stats.total) * 100)}%)</Typography>
                    </Box>
                  ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Excluded Images Summary */}
      {(excluded.blurry.length > 0 || excluded.notAlbumWorthy.length > 0 || excluded.lowQuality.length > 0) && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="warning.main">
              ⚠️ Images That Would Be Excluded by Common Filters
            </Typography>
            
            {excluded.blurry.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  <BlurIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Blurry ({excluded.blurry.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="100">Thumbnail</TableCell>
                        <TableCell>Filename</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excluded.blurry.slice(0, 10).map((img) => (
                        <TableRow key={img.imageId}>
                          <TableCell>
                            <AuthenticatedImage
                              imageId={img.imageId}
                              type="thumbnail"
                              alt={img.filename}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </TableCell>
                          <TableCell>{img.filename}</TableCell>
                          <TableCell><Chip label={img.reason} size="small" color="error" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {excluded.blurry.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ... and {excluded.blurry.length - 10} more
                  </Typography>
                )}
              </Box>
            )}

            {excluded.notAlbumWorthy.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  <AlbumIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Not Album Worthy ({excluded.notAlbumWorthy.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="100">Thumbnail</TableCell>
                        <TableCell>Filename</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Quality</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excluded.notAlbumWorthy.slice(0, 10).map((img) => (
                        <TableRow key={img.imageId}>
                          <TableCell>
                            <AuthenticatedImage
                              imageId={img.imageId}
                              type="thumbnail"
                              alt={img.filename}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </TableCell>
                          <TableCell>{img.filename}</TableCell>
                          <TableCell>{img.reason}</TableCell>
                          <TableCell>{img.qualityScore}/10</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {excluded.notAlbumWorthy.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ... and {excluded.notAlbumWorthy.length - 10} more
                  </Typography>
                )}
              </Box>
            )}

            {excluded.lowQuality.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  <QualityIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Low Quality &lt; 5/10 ({excluded.lowQuality.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="100">Thumbnail</TableCell>
                        <TableCell>Filename</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {excluded.lowQuality.slice(0, 10).map((img) => (
                        <TableRow key={img.imageId}>
                          <TableCell>
                            <AuthenticatedImage
                              imageId={img.imageId}
                              type="thumbnail"
                              alt={img.filename}
                              style={{
                                width: '80px',
                                height: '80px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            />
                          </TableCell>
                          <TableCell>{img.filename}</TableCell>
                          <TableCell><Chip label={img.reason} size="small" color="warning" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {excluded.lowQuality.length > 10 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    ... and {excluded.lowQuality.length - 10} more
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
